import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PeopleIcon from '@mui/icons-material/People';
import ShareIcon from '@mui/icons-material/Share';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';
import gameStateManager from '../services/GameStateManager';
import configService from '../services/ConfigService';
import shareService from '../services/ShareService';
import groupService from '../services/GroupService';
import LoadingSpinner from '../components/LoadingSpinner';
import CountdownTimer from '../components/CountdownTimer';
import ResultsTable from '../components/ResultsTable';
import TopTenReveal from '../components/TopTenReveal';
import ProvisionalResultsTable from '../components/ProvisionalResultsTable';
import ShareModal from '../components/ShareModal';
import WordCloud from '../components/WordCloud';
import DailyPlayerRanking from '../components/DailyPlayerRanking';
import GroupDailyRanking from '../components/GroupDailyRanking';
import { debugLog, debugError } from '../utils/debugUtils';

function computeDailyAchievements(wordResults, playersToday, streak) {
  const achievements = [];
  if (!wordResults || wordResults.length === 0) return achievements;

  const totalScore = wordResults.reduce((sum, r) => sum + (r.score || 0), 0);
  const scoredCount = wordResults.filter(r => r.score > 0).length;
  const topWordResult = wordResults.find(r => r.position === 1);
  const uniqueWord = wordResults.find(r => r.amount === 1 && r.score > 0);
  const coincidence = shareService.computeCoincidencePercent(wordResults, playersToday);

  if (totalScore === 15) {
    achievements.push({ emoji: '🎯', title: '¡Puntuación perfecta!', subtitle: '15/15 pts — ¡increíble!', key: 'perfect' });
  } else if (scoredCount === 5) {
    achievements.push({ emoji: '🏆', title: '¡5/5 palabras puntuaron!', subtitle: `${totalScore} pts · todas en el top 5`, key: 'allScored' });
  } else if (scoredCount > 0) {
    achievements.push({ emoji: '✅', title: `${scoredCount}/5 palabras puntuaron`, subtitle: `${totalScore} pts conseguidos hoy`, key: 'scored' });
  } else {
    achievements.push({ emoji: '📝', title: 'Has participado hoy', subtitle: 'Sin puntos esta vez — ¡mañana será mejor!', key: 'participated' });
  }

  if (topWordResult) {
    const w = topWordResult.word;
    const display = w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    achievements.push({ emoji: '🥇', title: `«${display}» fue la más jugada`, subtitle: `La eligieron ${topWordResult.amount} jugadores`, key: 'topWord' });
  }

  if (uniqueWord) {
    const w = uniqueWord.word;
    const display = w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    achievements.push({ emoji: '💡', title: `«${display}» — palabra única`, subtitle: '¡Solo tú la pensaste!', key: 'originalWord' });
  }

  if (coincidence !== null && coincidence >= 70 && !achievements.find(a => a.key === 'perfect')) {
    achievements.push({ emoji: '🤝', title: `Coincidiste con el ${coincidence}%`, subtitle: 'de los jugadores de hoy', key: 'coincidence' });
  }

  if (streak >= 2) {
    achievements.push({ emoji: '🔥', title: `¡Racha de ${streak} días!`, subtitle: '¡Sigue jugando cada día!', key: 'streak' });
  }

  return achievements;
}

const SSE_PROVISIONAL_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/game/provisional-results/stream`;
const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === 'true';

export default function ResultsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const isVisible = location.pathname === '/results';
  const preloaded = location.state?.preloaded ?? null;
  const [topic, setTopic] = useState(preloaded?.topic ?? null);
  const [finalResults, setFinalResults] = useState(preloaded?.finalResults ?? null);
  const [provisionalResults, setProvisionalResults] = useState(preloaded?.provisionalResults ?? null);
  const [yourWords, setYourWords] = useState(preloaded?.yourWords ?? []);
  const [loading, setLoading] = useState(!preloaded);
  const [isAfterScheduler, setIsAfterScheduler] = useState(preloaded?.isAfterScheduler ?? false);
  const [awaitingProcessing, setAwaitingProcessing] = useState(preloaded?.awaitingProcessing ?? false);
  const [playersCount, setPlayersCount] = useState(preloaded?.playersCount ?? null);
  const [schedulerTime, setSchedulerTime] = useState(preloaded?.schedulerTime ?? { hour: 22, minute: 5 });
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [userStreak, setUserStreak] = useState(preloaded?.userStreak ?? 0);
  const [myGroups, setMyGroups] = useState([]);
  const [sharingAchievementKey, setSharingAchievementKey] = useState(null);
  const [revealMode, setRevealMode] = useState(false);
  const [revealComplete, setRevealComplete] = useState(false);
  const [revealedTop10, setRevealedTop10] = useState([]);
  const [previousTopic, setPreviousTopic] = useState(preloaded?.previousData?.prevTopic ?? null);
  const [previousResults, setPreviousResults] = useState(preloaded?.previousData?.prevResults ?? null);
  const [previousYourWords, setPreviousYourWords] = useState(preloaded?.previousData?.prevWords ?? []);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const provisionalSseRef = useRef(null);
  const revealSseRef = useRef(null);
  const containerRef = useRef(null);
  const pullStartYRef = useRef(null);
  const pullDistRef = useRef(0);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const PULL_THRESHOLD = 72;
  const user = authService.getUser();

  const fetchPlayersCount = useCallback(async () => {
    try {
      const data = await apiService.get('/api/v1/game/players-today');
      setPlayersCount(data.count);
    } catch (err) {
      debugError('[ResultsPage] Error loading players count:', err);
    }
  }, []);

  const fetchProvisionalResults = useCallback(async () => {
    try {
      const data = await apiService.get('/api/v1/game/provisional-results');
      setProvisionalResults(Array.isArray(data) ? data : []);
      debugLog('[ResultsPage] Provisional results loaded:', data);
    } catch (err) {
      debugError('[ResultsPage] Error loading provisional results:', err);
    }
  }, []);

  const startRevealStream = useCallback((topicId, force = false) => {
    if (revealSseRef.current) revealSseRef.current.close();

    // Reiniciar el estado de reveal: el servidor siempre reenvía las palabras
    // desde el principio, así que si se reabre el stream (p. ej. al refrescar
    // durante la ventana de reveal) hay que descartar lo ya acumulado para no duplicarlo
    setRevealedTop10([]);
    setRevealComplete(false);

    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const forceParam = force && DEBUG_MODE ? '?force=true' : '';
    const url = `${API_BASE}/api/v1/game/reveal/${topicId}/stream${forceParam}`;
    debugLog(`[ResultsPage] Opening reveal SSE: ${url}`);
    const sse = new EventSource(url);
    revealSseRef.current = sse;

    sse.addEventListener('reveal', (e) => {
      debugLog('[ResultsPage] SSE reveal:', e.data);
      const word = JSON.parse(e.data);
      setRevealedTop10((prev) => prev.some((w) => w.id === word.id) ? prev : [...prev, word]);
    });

    sse.addEventListener('complete', () => {
      debugLog('[ResultsPage] SSE complete');
      sse.close();
      revealSseRef.current = null;
      setRevealComplete(true);
    });

    sse.onerror = (err) => {
      debugError('[ResultsPage] SSE error:', err);
      sse.close();
      revealSseRef.current = null;
      setRevealComplete(true);
    };
  }, []);

  const fetchFinalResults = useCallback(async (topicId) => {
    try {
      const data = await apiService.get(`/api/v1/game/final-results/${topicId}`);
      const results = data?.results ?? (Array.isArray(data) ? data : []);
      const revealWindowActive = data?.revealWindowActive ?? false;

      if (results.length > 0 || revealWindowActive) {
        setFinalResults(results);
        setAwaitingProcessing(false);
        window.dispatchEvent(new CustomEvent('results-ready'));

        if (revealWindowActive) {
          setRevealMode(true);
          startRevealStream(topicId);
        }
        return true;
      }
      return false;
    } catch (err) {
      debugError('[ResultsPage] Error loading final results:', err);
      return false;
    }
  }, [startRevealStream]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const resetPull = () => {
      el.style.touchAction = '';
      isPullingRef.current = false;
      pullDistRef.current = 0;
      pullStartYRef.current = null;
      setPullDistance(0);
    };

    const onStart = (e) => {
      if (el.scrollTop === 0 && !isRefreshingRef.current) {
        pullStartYRef.current = e.touches[0].clientY;
        // Sobreescribir touch-action:pan-y del CSS para que preventDefault funcione en Android
        el.style.touchAction = 'none';
      }
    };

    const onMove = (e) => {
      if (pullStartYRef.current === null) return;
      const delta = e.touches[0].clientY - pullStartYRef.current;
      if (delta > 0 && el.scrollTop === 0) {
        e.preventDefault();
        const dist = Math.min(delta * 0.5, PULL_THRESHOLD * 1.5);
        pullDistRef.current = dist;
        isPullingRef.current = true;
        setPullDistance(dist);
      } else if (delta < 0) {
        resetPull();
      }
    };

    const onEnd = async () => {
      if (!isPullingRef.current) { resetPull(); return; }
      const dist = pullDistRef.current;
      resetPull();

      if (dist >= PULL_THRESHOLD && !isRefreshingRef.current) {
        try { await Haptics.impact({ style: ImpactStyle.Light }); } catch {}
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        handleRefresh();
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', resetPull);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', resetPull);
    };
  }, [handleRefresh]);

  useEffect(() => {
    const load = async () => {
      // Si hay datos precargados desde AdPage, no volver a cargar en la primera visita
      if (preloaded && refreshKey === 0) {
        if (preloaded.finalResults) window.dispatchEvent(new CustomEvent('results-ready'));
        setLoading(false);
        return;
      }
      // Solo mostrar spinner de carga completa en la carga inicial
      if (refreshKey === 0) setLoading(true);
      try {
        const topicData = await apiService.get('/api/v1/topics/today');
        setTopic(topicData);

        if (user && topicData) {
          try {
            const serverWords = await apiService.get(`/api/v1/game/user-words/${topicData.id}`);
            setYourWords(serverWords || []);
          } catch {
            const words = await gameStateManager.getWords(user.id, topicData.id);
            setYourWords(words.map((raw) => ({ raw, canonical: null })));
          }
        }

        await fetchPlayersCount();

        const st = await configService.getSchedulerTimeAsync();
        setSchedulerTime(st);

        if (topicData) {
          // Siempre comprobamos primero si ya hay resultados calculados
          const hasResults = await fetchFinalResults(topicData.id);
          if (hasResults) {
            // Resultados ya disponibles (cómputo manual o scheduler ya ejecutado)
            setIsAfterScheduler(true);
          } else if (configService.isPastSchedulerTime()) {
            // Hora del scheduler superada pero aún procesando
            setIsAfterScheduler(true);
            setAwaitingProcessing(true);
          } else {
            // Día activo: mostrar ranking provisional y cuenta atrás
            await fetchProvisionalResults();
          }
        }
      } catch (err) {
        debugError('[ResultsPage] Error loading topic:', err);
      }

      if (user) {
        try {
          const streakData = await apiService.get('/api/v1/game/user-streak');
          setUserStreak(streakData?.streak || 0);
        } catch { /* no bloquea */ }

        try {
          const groups = await groupService.listMine();
          setMyGroups(Array.isArray(groups) ? groups : []);
        } catch { /* no bloquea */ }
      }

      try {
        const prevTopic = await apiService.get('/api/v1/topics/previous');
        if (prevTopic && prevTopic.hasResults) {
          setPreviousTopic(prevTopic);
          const [prevData, prevWords] = await Promise.all([
            apiService.get(`/api/v1/game/final-results/${prevTopic.id}`),
            user ? apiService.get(`/api/v1/game/user-words/${prevTopic.id}`) : Promise.resolve([]),
          ]);
          setPreviousResults(prevData?.results ?? (Array.isArray(prevData) ? prevData : []));
          setPreviousYourWords(prevWords || []);
        }
      } catch (err) {
        debugError('[ResultsPage] Error loading previous topic:', err);
      } finally {
        setLoading(false);
        if (refreshKey > 0) {
          isRefreshingRef.current = false;
          setIsRefreshing(false);
        }
      }
    };
    load();
  }, [user, fetchFinalResults, fetchPlayersCount, fetchProvisionalResults, refreshKey]);

  // Suscripción SSE al ranking provisional (reemplaza el polling cada 60s)
  useEffect(() => {
    if (isAfterScheduler) return;

    const sse = new EventSource(SSE_PROVISIONAL_URL);
    provisionalSseRef.current = sse;

    sse.addEventListener('update', () => {
      fetchProvisionalResults();
    });

    sse.onerror = () => {
      sse.close();
    };

    return () => {
      sse.close();
    };
  }, [isAfterScheduler, fetchProvisionalResults]);

  // Cerrar stream de reveal al desmontar
  useEffect(() => {
    return () => {
      revealSseRef.current?.close();
    };
  }, []);

  // Cuando se agota el contador, cargar resultados finales (con reintento si no están listos)
  const handleCountdownExpire = useCallback(async () => {
    setIsAfterScheduler(true);

    if (!topic) return;
    const hasResults = await fetchFinalResults(topic.id);
    if (!hasResults) {
      setAwaitingProcessing(true);
      // Reintentar cada 30 s hasta que el scheduler procese los resultados
      const retryInterval = setInterval(async () => {
        const ready = await fetchFinalResults(topic.id);
        if (ready) {
          setAwaitingProcessing(false);
          clearInterval(retryInterval);
        }
      }, 30_000);
    }
  }, [topic, fetchFinalResults]);

  const handleDebugReveal = useCallback(() => {
    if (!DEBUG_MODE) return;
    // Use previous topic when available — today's topic has no DailyResult entries yet
    const testTopicId = previousTopic?.id ?? topic?.id;
    debugLog(`[ResultsPage] handleDebugReveal: testTopicId=${testTopicId} previousTopic=${previousTopic?.id} topic=${topic?.id}`);
    if (!testTopicId) return;
    setRevealedTop10([]);
    setRevealComplete(false);
    setIsAfterScheduler(true);
    setAwaitingProcessing(false);
    setRevealMode(true);
    startRevealStream(testTopicId, true);
  }, [topic, previousTopic, startRevealStream]);

  const previousTopicSection = previousTopic && previousResults && previousResults.length > 0 ? (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {t('results.previousTopic')}: <strong>{previousTopic.topic}</strong>
      </Typography>
      <ResultsTable results={previousResults} yourWords={previousYourWords} showTitle={false} />
    </Box>
  ) : null;

  return (
    <Box ref={containerRef} className="page-content" sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
      {/* Indicador pull-to-refresh: baja con el dedo */}
      {(pullDistance > 0 || isRefreshing) && (
        <Box sx={{
          position: 'fixed',
          top: isRefreshing ? 72 : 72 + pullDistance,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          bgcolor: 'white',
          borderRadius: '50%',
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 3,
          opacity: isRefreshing ? 1 : Math.max(0.4, pullDistance / PULL_THRESHOLD),
          transition: pullDistance > 0 ? 'none' : 'top 0.3s ease, opacity 0.2s ease',
        }}>
          <CircularProgress
            size={26}
            variant={isRefreshing ? 'indeterminate' : 'determinate'}
            value={Math.min((pullDistance / PULL_THRESHOLD) * 100, 100)}
          />
        </Box>
      )}

      {/* Wrapper que se desplaza hacia abajo durante el gesto */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : 'translateY(0)',
        transition: pullDistance > 0 ? 'none' : 'transform 0.3s ease',
      }}>
      {loading ? <LoadingSpinner /> : <>
      <Typography variant="h5" fontWeight={700} textAlign="center" color="white" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
        {t('results.title')}
      </Typography>

      {DEBUG_MODE && topic && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleDebugReveal}
          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', py: 0.5 }}
        >
          🧪 Test Reveal (debug)
        </Button>
      )}

      {playersCount !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'white' }}>
          <PeopleIcon fontSize="small" sx={{ opacity: 0.85 }} />
          <Typography variant="body2" sx={{ opacity: 0.85, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            {t('results.playersToday', { count: playersCount })}
          </Typography>
        </Box>
      )}

      {!isAfterScheduler ? (
        // ── FASE PROVISIONAL: mientras el día sigue activo ──
        <Card sx={{ width: '100%', flexShrink: 0 }}>
          <CardContent sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('results.countdownTitle')}
              </Typography>
              {topic && (
                <CountdownTimer
                  targetHour={schedulerTime.hour}
                  targetMinute={schedulerTime.minute}
                  onExpire={handleCountdownExpire}
                />
              )}
            </Box>

            {yourWords.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {t('results.yourWordsToday')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, maxHeight: 120, overflowY: 'auto' }}>
                  {yourWords.map((word, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        bgcolor: 'primary.light',
                        color: 'white',
                        borderRadius: 4,
                        px: 2,
                        py: 0.5,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                      }}
                    >
                      {word.raw}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {provisionalResults && provisionalResults.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <WordCloud
                  results={provisionalResults}
                  yourWords={yourWords}
                  blurOthers
                />
                <Divider sx={{ my: 2 }} />
                <ProvisionalResultsTable results={provisionalResults} />
              </>
            )}
            {previousTopicSection}
          </CardContent>
        </Card>
      ) : awaitingProcessing ? (
        // ── FASE DE ESPERA: día cerrado, resultados aún procesándose ──
        <Card sx={{ width: '100%', flexShrink: 0 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('results.processingTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('results.processingMessage')}
              </Typography>
            </Box>
            {previousTopicSection}
          </CardContent>
        </Card>
      ) : (
        // ── FASE FINAL: resultados definitivos (top 100) ──
        <Card sx={{ width: '100%', flexShrink: 0 }}>
          <CardContent>
            {(finalResults && finalResults.length > 0) || revealMode ? (
              <>
                {revealMode ? (
                  <>
                    <TopTenReveal
                      revealedWords={revealedTop10}
                      yourWords={yourWords}
                      totalToReveal={10}
                      isComplete={revealComplete}
                      onComplete={() => setRevealMode(false)}
                      enableConfetti={isVisible}
                    />
                    {finalResults && finalResults.length > 0 && (
                      <ResultsTable
                        results={finalResults}
                        yourWords={yourWords}
                        showTitle={false}
                        excludeTop={10}
                        enableConfetti={isVisible}
                      />
                    )}
                  </>
                ) : (
                  <ResultsTable
                    results={[
                      ...revealedTop10.filter((r) => !(finalResults ?? []).some((f) => f.position === r.position)),
                      ...(finalResults ?? []),
                    ].sort((a, b) => a.position - b.position)}
                    yourWords={yourWords}
                    enableConfetti={isVisible}
                  />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5, mb: 0.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareModalOpen(true)}
                    sx={{
                      background: 'linear-gradient(90deg, #1976d2, #764ba2)',
                      color: 'white',
                      fontWeight: 700,
                      px: 3,
                      borderRadius: 3,
                      '&:hover': { background: 'linear-gradient(90deg, #1565c0, #6a1b9a)' },
                    }}
                  >
                    {t('share.button')}
                  </Button>
                </Box>

                {/* Logros del día */}
                {(() => {
                  const allResults = [
                    ...revealedTop10.filter((r) => !(finalResults ?? []).some((f) => f.position === r.position)),
                    ...(finalResults ?? []),
                  ];
                  const wordResults = shareService.computeWordResults(yourWords, allResults);
                  const achievements = computeDailyAchievements(wordResults, playersCount, userStreak);
                  if (achievements.length === 0) return null;
                  return (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {t('dailyAchievements.title')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {achievements.map(ach => (
                          <Box
                            key={ach.key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              p: 1.2,
                              borderRadius: 2,
                              bgcolor: 'rgba(25,118,210,0.06)',
                              border: '1px solid rgba(25,118,210,0.15)',
                            }}
                          >
                            <Typography fontSize="1.8rem" lineHeight={1} sx={{ flexShrink: 0 }}>
                              {ach.emoji}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                {ach.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ach.subtitle}
                              </Typography>
                            </Box>
                            <Tooltip title={t('dailyAchievements.share')}>
                              <IconButton
                                size="small"
                                disabled={sharingAchievementKey === ach.key}
                                onClick={async () => {
                                  setSharingAchievementKey(ach.key);
                                  try {
                                    await shareService.shareAchievement({
                                      emoji: ach.emoji,
                                      title: ach.title,
                                      subtitle: ach.subtitle,
                                      topic: topic?.topic,
                                      topicDate: topic?.date,
                                    });
                                  } finally {
                                    setSharingAchievementKey(null);
                                  }
                                }}
                                sx={{ color: 'primary.main', flexShrink: 0 }}
                              >
                                {sharingAchievementKey === ach.key
                                  ? <CircularProgress size={18} />
                                  : <ShareIcon fontSize="small" />
                                }
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    </>
                  );
                })()}
              </>
            ) : (
              <Typography textAlign="center" color="text.secondary" py={4}>
                {t('results.noResults')}
              </Typography>
            )}
            <Divider sx={{ my: 3 }} />
            {finalResults && finalResults.length > 0 && (
              <>
                <WordCloud results={finalResults} yourWords={yourWords} />
                <Divider sx={{ my: 3 }} />
              </>
            )}
            <DailyPlayerRanking date={topic?.date} user={user} />
            {myGroups.map((g) => (
              <Box key={g.id}>
                <Divider sx={{ my: 3 }} />
                <GroupDailyRanking groupId={g.id} groupName={g.name} date={topic?.date} user={user} />
              </Box>
            ))}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('results.nextTopicTitle')}
              </Typography>
              <CountdownTimer
                targetHour={schedulerTime.hour}
                targetMinute={schedulerTime.minute}
                onExpire={() => {}}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        topic={topic?.topic}
        topicDate={topic?.date}
        userWords={yourWords}
        finalResults={finalResults || []}
        playersToday={playersCount}
      />
      </>}
      </Box>
    </Box>
  );
}
